<script setup lang="ts">
  import { useThemeConfig } from '@core/composable/useThemeConfig';
  import type { ThemeSwitcherTheme } from '@layouts/types';

  const props = defineProps<{
    themes: ThemeSwitcherTheme[];
  }>();

  const { theme } = useThemeConfig();

  const {
    state: currentThemeName,
    next: getNextThemeName,
    index: currentThemeIndex
  } = useCycleList(
    props.themes.map(t => t.name),
    { initialValue: theme.value }
  );

  const changeTheme = async (event: MouseEvent) => {
    const nextName = getNextThemeName();
    const doc = document as Document & { startViewTransition?: (cb: () => Promise<void>) => { ready: Promise<void> } };

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (!doc.startViewTransition || reduceMotion) {
      theme.value = nextName;
      return;
    }

    // Circular reveal expanding from the clicked button
    const x = event.clientX;
    const y = event.clientY;
    const endRadius = Math.hypot(Math.max(x, innerWidth - x), Math.max(y, innerHeight - y));

    const transition = doc.startViewTransition(async () => {
      theme.value = nextName;
      await nextTick();
    });

    await transition.ready;

    document.documentElement.animate(
      { clipPath: [`circle(0px at ${x}px ${y}px)`, `circle(${endRadius}px at ${x}px ${y}px)`] },
      { duration: 500, easing: 'ease-in-out', pseudoElement: '::view-transition-new(root)' }
    );
  };

  // Update icon if theme is changed from other sources
  watch(theme, val => {
    currentThemeName.value = val;
  });
</script>

<template>
  <IconBtn @click="changeTheme">
    <Transition name="theme-icon" mode="out-in">
      <VIcon :key="props.themes[currentThemeIndex].icon" size="26" :icon="props.themes[currentThemeIndex].icon" />
    </Transition>
    <VTooltip activator="parent" open-delay="1000" scroll-strategy="close">
      <span class="text-capitalize">{{ currentThemeName }}</span>
    </VTooltip>
  </IconBtn>
</template>

<style>
  /* Disable the default cross-fade; the circular reveal is driven from JS */
  ::view-transition-old(root),
  ::view-transition-new(root) {
    animation: none;
    mix-blend-mode: normal;
  }
</style>

<style scoped>
  .theme-icon-enter-active,
  .theme-icon-leave-active {
    transition:
      transform 0.25s ease,
      opacity 0.25s ease;
  }

  .theme-icon-enter-from {
    opacity: 0;
    transform: rotate(-90deg) scale(0.6);
  }

  .theme-icon-leave-to {
    opacity: 0;
    transform: rotate(90deg) scale(0.6);
  }
</style>
